import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import S3Repository from 'repositories/S3Repository'
import type { RootState } from 'store/modules/rootReducer'
import { getLineBearerToken } from 'utils/bearerToken'
import { isAuthenticatedS3URL } from 'utils/s3Authenticated'

const blankImagePath =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const imageCache = new Map<string, string>()

export const useImage = (s3Url: string | null | undefined) => {
  const reduxToken = useSelector((s: RootState) => s.auth?.token || '')
  const hasToken = !!(getLineBearerToken() || reduxToken)

  const [imageUrl, setImageUrl] = useState<string>(() => {
    if (!s3Url) return blankImagePath
    if (!isAuthenticatedS3URL(s3Url)) return s3Url
    return imageCache.get(s3Url) || blankImagePath
  })

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!s3Url || !isAuthenticatedS3URL(s3Url) || imageCache.has(s3Url)) return false
    return true
  })

  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!s3Url) {
      setImageUrl(blankImagePath)
      setIsLoading(false)
      return
    }

    if (!isAuthenticatedS3URL(s3Url)) {
      setImageUrl(s3Url)
      setIsLoading(false)
      return
    }

    /** Nunca usar URL crua do bucket privado no DOM — evita 403 no browser. */
    if (!hasToken) {
      setImageUrl(blankImagePath)
      setIsLoading(false)
      return
    }

    if (imageCache.has(s3Url)) {
      setImageUrl(imageCache.get(s3Url) || blankImagePath)
      setIsLoading(false)
      return
    }

    const fetchAndSetImage = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const s3Blob = await S3Repository.getImage(s3Url)
        const isSVGStream =
          s3Blob.type === 'application/octet-stream' && s3Url.endsWith('.svg')

        let finalBlob = s3Blob
        if (s3Blob.type === 'application/octet-stream') {
          if (s3Url.endsWith('.svg')) {
            finalBlob = new Blob([s3Blob], { type: 'image/svg+xml' })
          } else if (s3Url.endsWith('.pdf')) {
            finalBlob = new Blob([s3Blob], { type: 'application/pdf' })
          } else if (s3Url.endsWith('.image')) {
            finalBlob = new Blob([s3Blob], { type: 'image/png' })
          } else if (s3Url.match(/\.(png|jpg|jpeg|webp)$/i)) {
            finalBlob = new Blob([s3Blob], { type: 'image/png' })
          }
        } else if (isSVGStream) {
          let mimeType = 'image/png'
          if (s3Url.match(/\.jpe?g$/i)) {
            mimeType = 'image/jpeg'
          } else if (s3Url.match(/\.webp$/i)) {
            mimeType = 'image/webp'
          } else if (s3Url.match(/\.png$/i)) {
            mimeType = 'image/png'
          }
          finalBlob = new Blob([s3Blob], { type: mimeType })
        }

        const created = URL.createObjectURL(finalBlob)
        imageCache.set(s3Url, created)
        setImageUrl(created)
      } catch (err: any) {
        console.error(err)
        setImageUrl(blankImagePath)
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAndSetImage()
  }, [s3Url, hasToken, reduxToken])

  return { imageUrl, isLoading, error }
}
